import React, { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TopBar from "../components/common/TopBar";
import "./TermsOfUsePage.css";

const SECTION_CONFIG = [
  { id: "overview", key: "overview" },
  { id: "eligibility", key: "eligibility" },
  { id: "account", key: "account" },
  { id: "verification", key: "verification" },
  { id: "conduct", key: "conduct" },
  { id: "payments-economy", key: "paymentsEconomy" },
  { id: "virtual-items", key: "virtualItems" },
  { id: "communications", key: "communications" },
  { id: "withdrawals", key: "withdrawals" },
  { id: "moderation", key: "moderation" },
  { id: "suspension", key: "suspension" },
  { id: "intellectual-property", key: "intellectualProperty" },
  { id: "beta-availability", key: "betaAvailability" },
  { id: "liability", key: "liability" },
  { id: "changes-privacy", key: "changesPrivacy" },
];

function splitParagraphs(text) {
  if (!text) return [];
  return String(text)
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

export default function TermsOfUsePage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const isRtl = useMemo(() => {
    const lng = String(i18n.language || "").toLowerCase();
    return lng.startsWith("ar") || lng.startsWith("fa") || lng.startsWith("he");
  }, [i18n.language]);

  const scrollToId = useCallback((hashId) => {
    const el = document.getElementById(hashId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const pageTitle = t("legal.termsOfUse.pageTitle", {
    defaultValue: "Terms of use",
  });

  const privacyTitle = t("legal.privacyPolicy.pageTitle", {
    defaultValue: "Privacy policy",
  });

  return (
    <div className="pp-page" dir={isRtl ? "rtl" : "ltr"} lang={i18n.language}>
      <TopBar title={pageTitle} onBack={() => navigate(-1)} />

      <div className="pp-inner">
        <header className="pp-hero">
          <span className="pp-badge" aria-hidden>
            Livora
          </span>
          <h1 className="pp-title">{pageTitle}</h1>
          <p className="pp-updated">
            {t("legal.termsOfUse.lastUpdated", {
              defaultValue: "Last updated: May 16, 2026",
            })}
          </p>
          <p className="pp-lead">
            {t("legal.termsOfUse.lead", {
              defaultValue:
                "These Terms of Use (\"Terms\") govern your access to and use of Livora (\"the platform\", \"we\", \"us\"). By creating an account or using Livora, you agree to these Terms and to our Privacy policy.",
            })}
          </p>
        </header>

        <nav
          className="pp-nav-wrap"
          aria-label={t("legal.termsOfUse.navLabel", { defaultValue: "On this page" })}
        >
          <div className="pp-nav">
            {SECTION_CONFIG.map(({ id, key }) => (
              <button
                key={id}
                type="button"
                className="pp-nav-link"
                onClick={() => scrollToId(`tu-${id}`)}
              >
                {t(`legal.termsOfUse.nav.${key}`, {
                  defaultValue: key,
                })}
              </button>
            ))}
          </div>
        </nav>

        <main>
          {SECTION_CONFIG.map(({ id, key }) => {
            const title = t(`legal.termsOfUse.sections.${key}.title`, {
              defaultValue: "",
            });
            const body = t(`legal.termsOfUse.sections.${key}.body`, {
              defaultValue: "",
            });
            const bulletsRaw = t(`legal.termsOfUse.sections.${key}.bullets`, {
              defaultValue: [],
              returnObjects: true,
            });
            const bullets =
              Array.isArray(bulletsRaw) && bulletsRaw.every((x) => typeof x === "string")
                ? bulletsRaw
                : [];

            return (
              <section
                key={id}
                id={`tu-${id}`}
                className="pp-section"
                aria-labelledby={`tu-h-${id}`}
              >
                <article className="pp-card">
                  <h2 id={`tu-h-${id}`}>{title}</h2>
                  {splitParagraphs(body).map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                  {bullets.length > 0 ? (
                    <ul className="pp-list">
                      {bullets.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              </section>
            );
          })}
        </main>

        <footer className="pp-footnote">
          {t("legal.termsOfUse.disclaimer", {
            defaultValue:
              "These Terms are not legal advice. If you do not agree, stop using Livora. For support, use in-app help channels.",
          })}
          <div className="tu-privacy-row">
            {t("legal.termsOfUse.privacyRefIntro", {
              defaultValue: "How we handle personal data is described in the",
            })}{" "}
            <Link className="tu-privacy-link" to="/privacy">
              {privacyTitle}
            </Link>
            {t("legal.termsOfUse.privacyRefOutro", { defaultValue: "." })}
          </div>
        </footer>
      </div>
    </div>
  );
}
